import { useState, useEffect, useCallback } from "react";
import { getFlightTimelineEvents, getFlightTimelineStatistics, getFlightTimelineCategories } from "../api/flightOperationApi";

/**
 * 航班历史时间线数据管理Hook
 * @param {number} flightId - 航班ID
 * @param {Object} options - 配置选项
 * @param {boolean} options.include_business_entity - 是否包含业务主体的所有航班事件（默认true）
 * @returns {Object} 包含时间线数据和操作方法的对象
 */
const useFlightHistory = (flightId, options = {}) => {
    const { include_business_entity = true } = options;

    // 基础数据状态
    const [events, setEvents] = useState([]);
    const [statistics, setStatistics] = useState({
        all: 0,
        plan: 0,
        transaction: 0,
        communication: 0,
        system: 0,
    });
    const [categories, setCategories] = useState([]);
    const [total, setTotal] = useState(0);

    // 业务主体相关状态
    const [businessEntityEnabled, setBusinessEntityEnabled] = useState(include_business_entity);
    const [businessEntityId, setBusinessEntityId] = useState(null);
    const [flightChainInfo, setFlightChainInfo] = useState(null);

    // 加载状态
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [statisticsLoading, setStatisticsLoading] = useState(false);

    // 错误状态
    const [error, setError] = useState(null);

    // 过滤和分页状态
    const [filters, setFilters] = useState({
        category: "all",
        search_query: "",
        start_date: null,
        end_date: null,
        sort_desc: true,
    });

    const [pagination, setPagination] = useState({
        page: 1,
        page_size: 200,
        hasMore: true,
    });

    // 获取事件列表
    const fetchEvents = useCallback(
        async (resetPagination = false) => {
            if (!flightId) return;

            try {
                const isFirstLoad = resetPagination || pagination.page === 1;
                setLoading(isFirstLoad);
                setLoadingMore(!isFirstLoad);
                setError(null);

                const currentPage = resetPagination ? 1 : pagination.page;

                const params = {
                    ...filters,
                    page: currentPage,
                    page_size: pagination.page_size,
                    include_business_entity: businessEntityEnabled,
                };

                // 过滤掉空值
                Object.keys(params).forEach((key) => {
                    if (params[key] === null || params[key] === undefined || params[key] === "") {
                        delete params[key];
                    }
                });

                console.log("📊 fetchEvents调用:", {
                    flightId,
                    currentPage,
                    resetPagination,
                    businessEntityEnabled,
                    params,
                });

                const response = await getFlightTimelineEvents(flightId, params);
                const { items, total: totalCount } = response.data;

                console.log("📊 API响应:", {
                    itemsCount: items.length,
                    totalCount,
                    firstItemId: items[0]?.id,
                    lastItemId: items[items.length - 1]?.id,
                });

                // 提取业务主体信息
                if (items.length > 0) {
                    const firstEvent = items[0];
                    if (firstEvent.business_entity_id) {
                        setBusinessEntityId(firstEvent.business_entity_id);
                    }

                    // 收集航班链信息
                    const flightInfoMap = new Map();
                    items.forEach((event) => {
                        if (event.flight_info && event.flight_id) {
                            flightInfoMap.set(event.flight_id, event.flight_info);
                        }
                    });

                    if (flightInfoMap.size > 0) {
                        setFlightChainInfo(Object.fromEntries(flightInfoMap));
                    }
                }

                if (resetPagination) {
                    // 重置分页时，直接设置新数据
                    console.log("📊 重置分页，设置新数据");
                    setEvents(items);
                    setPagination((prev) => ({
                        ...prev,
                        page: 1,
                        hasMore: items.length >= prev.page_size && items.length < totalCount,
                    }));
                } else {
                    // 加载更多时，需要去重合并
                    console.log("📊 加载更多，合并数据");
                    setEvents((prev) => {
                        // 创建现有事件的ID集合
                        const existingIds = new Set(prev.map((event) => event.id));
                        // 过滤掉已存在的事件
                        const newItems = items.filter((item) => !existingIds.has(item.id));
                        console.log("📊 去重结果:", {
                            原有事件数: prev.length,
                            新事件数: items.length,
                            去重后新事件数: newItems.length,
                            重复事件数: items.length - newItems.length,
                        });
                        return [...prev, ...newItems];
                    });

                    const nextPage = currentPage + 1;
                    const currentTotal = (currentPage - 1) * pagination.page_size + items.length;
                    setPagination((prev) => ({
                        ...prev,
                        page: nextPage,
                        hasMore: currentTotal < totalCount,
                    }));
                }

                setTotal(totalCount);
            } catch (err) {
                console.error("获取航班时间线事件失败:", err);
                setError(err.message || "获取事件失败");
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [flightId, filters, pagination.page_size, businessEntityEnabled],
    ); // 包含businessEntityEnabled依赖

    // 添加一个useEffect来监听过滤条件变化
    useEffect(() => {
        if (flightId && events.length === 0) {
            // 只有在事件为空时才自动加载（避免重复加载）
            console.log("📊 过滤条件变化，自动重新加载数据");
            fetchEvents(true);
        }
    }, [flightId, filters, businessEntityEnabled, fetchEvents]); // 监听关键过滤条件变化

    // 获取统计信息
    const fetchStatistics = useCallback(async () => {
        if (!flightId) return;

        try {
            setStatisticsLoading(true);
            const response = await getFlightTimelineStatistics(flightId, businessEntityEnabled);
            setStatistics(response.data);
        } catch (err) {
            console.error("获取航班时间线统计失败:", err);
        } finally {
            setStatisticsLoading(false);
        }
    }, [flightId, businessEntityEnabled]);

    // 获取分类列表
    const fetchCategories = useCallback(async () => {
        if (!flightId) return;

        try {
            const response = await getFlightTimelineCategories(flightId);
            setCategories(response.data);
        } catch (err) {
            console.error("获取航班时间线分类失败:", err);
        }
    }, [flightId]);

    // 切换业务主体模式
    const toggleBusinessEntity = useCallback(
        (enabled) => {
            console.log("📊 切换业务主体模式:", {
                当前状态: businessEntityEnabled,
                新状态: enabled,
            });

            setBusinessEntityEnabled(enabled);
            // 重置分页状态
            setPagination((prev) => ({
                ...prev,
                page: 1,
                hasMore: true,
            }));
            // 清空现有事件，强制重新加载
            setEvents([]);
            setTotal(0);
            setBusinessEntityId(null);
            setFlightChainInfo(null);

            // 立即重新加载数据
            setTimeout(() => {
                fetchEvents(true);
            }, 100);
        },
        [fetchEvents],
    );

    // 搜索查询设置
    const setSearchQuery = useCallback(
        (query) => {
            setFilters((prev) => ({
                ...prev,
                search_query: query,
            }));
            setPagination((prev) => ({
                ...prev,
                page: 1,
                hasMore: true,
            }));
            // 清空现有事件，并立即重新加载
            setEvents([]);
            // 使用setTimeout确保状态更新后再加载
            setTimeout(() => {
                fetchEvents(true);
            }, 50);
        },
        [fetchEvents],
    );

    // 更新过滤条件
    const updateFilters = useCallback(
        (newFilters) => {
            setFilters((prev) => ({
                ...prev,
                ...newFilters,
            }));
            setPagination((prev) => ({
                ...prev,
                page: 1,
                hasMore: true,
            }));
            // 清空现有事件，并立即重新加载
            setEvents([]);
            // 使用setTimeout确保状态更新后再加载
            setTimeout(() => {
                fetchEvents(true);
            }, 50);
        },
        [fetchEvents],
    );

    // 重置过滤条件
    const resetFilters = useCallback(() => {
        setFilters({
            category: "all",
            search_query: "",
            start_date: null,
            end_date: null,
            sort_desc: true,
        });
        setPagination({
            page: 1,
            page_size: 200,
            hasMore: true,
        });
        // 清空现有事件
        setEvents([]);
    }, []);

    // 加载更多
    const loadMore = useCallback(() => {
        if (!loadingMore && pagination.hasMore) {
            // 直接调用fetchEvents，传入当前的page + 1
            const nextPage = pagination.page;
            setPagination((prev) => ({ ...prev, page: nextPage + 1 }));

            // 手动构建参数并调用API
            const loadMoreData = async () => {
                try {
                    setLoadingMore(true);
                    setError(null);

                    const params = {
                        ...filters,
                        page: nextPage,
                        page_size: pagination.page_size,
                        include_business_entity: businessEntityEnabled,
                    };

                    // 过滤掉空值
                    Object.keys(params).forEach((key) => {
                        if (params[key] === null || params[key] === undefined || params[key] === "") {
                            delete params[key];
                        }
                    });

                    const response = await getFlightTimelineEvents(flightId, params);
                    const { items, total: totalCount } = response.data;

                    // 追加新事件
                    setEvents((prev) => [...prev, ...items]);

                    // 更新分页状态
                    const currentTotal = (nextPage - 1) * pagination.page_size + items.length;
                    setPagination((prev) => ({
                        ...prev,
                        hasMore: currentTotal < totalCount,
                    }));

                    setTotal(totalCount);
                } catch (err) {
                    console.error("加载更多事件失败:", err);
                    setError(err.message || "加载更多失败");
                } finally {
                    setLoadingMore(false);
                }
            };

            loadMoreData();
        }
    }, [flightId, filters, pagination, businessEntityEnabled, loadingMore]);

    // 刷新数据
    const refresh = useCallback(() => {
        setEvents([]);
        setPagination((prev) => ({ ...prev, page: 1, hasMore: true }));
        fetchEvents(true);
        fetchStatistics();
    }, [fetchEvents, fetchStatistics]);

    // 初始化数据加载 - 只在flightId变化时触发
    useEffect(() => {
        if (flightId) {
            console.log("📊 初始化数据加载, flightId:", flightId);
            setEvents([]);
            setPagination({ page: 1, page_size: 200, hasMore: true });
            fetchEvents(true);
            fetchStatistics();
            fetchCategories();
        }
    }, [flightId, fetchEvents, fetchStatistics, fetchCategories]); // 包含所有必要的依赖项

    // 移除过滤条件变化的useEffect，因为updateFilters已经会清空events并触发重新加载

    return {
        // 数据
        events,
        statistics,
        categories,
        total,

        // 业务主体相关
        businessEntityEnabled,
        businessEntityId,
        flightChainInfo,

        // 状态
        loading,
        loadingMore,
        statisticsLoading,
        error,

        // 过滤和分页
        filters,
        pagination,

        // 操作方法
        updateFilters,
        resetFilters,
        loadMore,
        refresh,
        setSearchQuery,
        toggleBusinessEntity,
    };
};

export default useFlightHistory;
